export const KPI_LABEL_KEYS = {
  client_with_activity: 'TEAM_PERFORMANCE.KPIS.CLIENT_WITH_ACTIVITY',
  preferred_fixed_client: 'TEAM_PERFORMANCE.KPIS.PREFERRED_FIXED_CLIENT',
  finished_visits: 'TEAM_PERFORMANCE.KPIS.FINISHED_VISITS',
  total_booked_visits: 'TEAM_PERFORMANCE.KPIS.TOTAL_BOOKED_VISITS',
  visits_booked_by_agent: 'TEAM_PERFORMANCE.KPIS.VISITS_BOOKED_BY_AGENT',
  sent_offers: 'TEAM_PERFORMANCE.KPIS.SENT_OFFERS',
  accepted_offers: 'TEAM_PERFORMANCE.KPIS.ACCEPTED_OFFERS',
  signed_ccv: 'TEAM_PERFORMANCE.KPIS.SIGNED_CCV',
};

export const LEADERBOARD_METRICS = [
  'finished_visits',
  'signed_ccv',
  'sent_offers',
  'accepted_offers',
  'client_with_activity',
];

export const TABLE_COLUMNS = [
  { key: 'name', labelKey: 'TEAM_PERFORMANCE.COLUMNS.AGENT', sortable: true },
  {
    key: 'client_with_activity',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.CLIENTS',
    sortable: true,
  },
  {
    key: 'finished_visits',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.FINISHED_VISITS',
    sortable: true,
  },
  {
    key: 'total_booked_visits',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.BOOKED_VISITS',
    sortable: true,
  },
  {
    key: 'sent_offers',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.SENT_OFFERS',
    sortable: true,
  },
  {
    key: 'accepted_offers',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.ACCEPTED_OFFERS',
    sortable: true,
  },
  {
    key: 'signed_ccv',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.SIGNED_CCV',
    sortable: true,
  },
  {
    key: 'target',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.TARGET',
    sortable: false,
  },
  {
    key: 'progress',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.PROGRESS',
    sortable: true,
  },
  {
    key: 'delta.finished_visits',
    labelKey: 'TEAM_PERFORMANCE.COLUMNS.TREND',
    sortable: true,
  },
];

export const PRODUCTIVITY_GROUPS = [
  {
    id: 'clients',
    labelKey: 'TEAM_PERFORMANCE.GROUPS.CLIENTS',
    icon: 'i-lucide-users',
    keys: ['client_with_activity', 'preferred_fixed_client'],
  },
  {
    id: 'visits',
    labelKey: 'TEAM_PERFORMANCE.GROUPS.VISITS',
    icon: 'i-lucide-calendar-check',
    keys: ['finished_visits', 'total_booked_visits', 'visits_booked_by_agent'],
  },
  {
    id: 'offers',
    labelKey: 'TEAM_PERFORMANCE.GROUPS.OFFERS',
    icon: 'i-lucide-file-check-2',
    keys: ['sent_offers', 'accepted_offers', 'signed_ccv'],
  },
];

export function currentMonthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function periodRange(key) {
  const [year, month] = key.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();

  return {
    since: `${key}-01T00:00:00`,
    until: `${key}-${String(lastDay).padStart(2, '0')}T23:59:59`,
  };
}

export function recentMonthOptions(total = 8, baseDate = new Date()) {
  return Array.from({ length: total }, (_, index) => {
    const date = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth() - index,
      1
    );
    const value = currentMonthKey(date);
    const label = date.toLocaleDateString('pt-BR', {
      month: 'long',
      year: 'numeric',
    });

    return {
      value,
      label: label.charAt(0).toUpperCase() + label.slice(1),
    };
  });
}

export function normalizeTeamPerformanceResponse(response = {}) {
  const payload = response.data || response;
  const meta = payload.meta || {};

  return {
    rows: Array.isArray(payload.data) ? payload.data : [],
    meta: {
      current_page: Number(meta.current_page || 1),
      total_count: Number(meta.total_count || 0),
      total_pages: Number(meta.total_pages || 1),
      per_page: Number(meta.per_page || 25),
      totals: meta.totals || {},
      team_avg_progress: Number(meta.team_avg_progress || 0),
      period: meta.period || {},
      previous_period: meta.previous_period || {},
    },
  };
}

export function metricValue(row, key) {
  if (key === 'name') return row?.user?.name || '';
  if (key === 'progress') return Number(row?.progress || 0);
  if (key === 'target') return Number(row?.target?.target_value || 0);
  if (key.startsWith('delta.')) {
    return Number(row?.delta?.[key.replace('delta.', '')] || 0);
  }

  return Number(row?.kpis?.[key] || 0);
}

export function sortLeaderboardRows(rows = [], metric = 'finished_visits') {
  return [...rows].sort((left, right) => {
    const diff = metricValue(right, metric) - metricValue(left, metric);
    if (diff !== 0) return diff;

    return metricValue(left, 'name').localeCompare(metricValue(right, 'name'));
  });
}

export function kpiLabelKey(key) {
  return KPI_LABEL_KEYS[key] || key;
}

export function productivityGroupsForRow(row = {}, labels = {}) {
  const kpiLabel = labels.kpiLabel || kpiLabelKey;
  const groupLabel = labels.groupLabel || (group => group.labelKey);

  return PRODUCTIVITY_GROUPS.map(group => ({
    ...group,
    label: groupLabel(group),
    metrics: group.keys.map(key => ({
      key,
      label: kpiLabel(key),
      value: metricValue(row, key),
      delta: metricValue(row, `delta.${key}`),
    })),
  }));
}

export function hasAnyKpi(rows = []) {
  return rows.some(row =>
    Object.values(row.kpis || {}).some(value => Number(value || 0) > 0)
  );
}
